import { z } from 'zod';
import { isCanonicalBase64,assertNoDuplicateJsonKeys } from './maintenanceJsonValidation';
import { maintenanceIdentityBootstrapSchema,validateMaintenanceIdentityBootstrap } from './maintenanceIdentityBootstrap';
import { maintenanceRepairBootstrapSchema, validateMaintenanceRepairBootstrap } from './maintenanceRepairBootstrap';
import type {
  MaintenancePolicy,
  MaintenancePolicyInput,
  MaintenanceTask,
} from './maintenanceTypes';
export const MAINTENANCE_PROTOCOL_VERSION = 1 as const;
export const DEFAULT_PREFER_HOST_WAIT_SECONDS = 1_800;
export const MIN_PREFER_HOST_WAIT_SECONDS = 1;
export const MAX_PREFER_HOST_WAIT_SECONDS = 604_800;
export const MAX_MAINTENANCE_PAYLOAD_BYTES = 256 * 1024;
export const MAX_MAINTENANCE_POWERSHELL_BYTES = 16 * 1024 * 1024;
export const MAX_START_GRANT_TTL_MS = 60_000;
export const MAINTENANCE_TASK_DOMAIN = 'playcast-maintenance-task:v1\n';
export const MAINTENANCE_START_DOMAIN = 'playcast-maintenance-start:v1\n';
export const MAINTENANCE_CANCEL_DOMAIN = 'playcast-maintenance-cancel:v1\n';
export const MAINTENANCE_RECONCILE_DOMAIN = 'playcast-maintenance-reconcile:v1\n';

const boundedId = z.string().min(1).max(128);
const machineId = z.string().min(1).max(256);
const nonnegativeSafeInteger = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const positiveSafeInteger = z.number().int().min(1).max(Number.MAX_SAFE_INTEGER);
export const maintenanceSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const maintenanceUtcTimestampSchema = z.string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)
  .refine((value) => !Number.isNaN(Date.parse(value)), 'Invalid UTC timestamp');

export const maintenanceIdSchema = z.object({
  machineId,
  jobId: boundedId,
  executionId: boundedId,
  stepId: boundedId,
  attemptId: boundedId,
  revision: nonnegativeSafeInteger,
}).strict();

export const maintenancePolicyInputSchema = z.discriminatedUnion('hostMode', [
  z.object({ hostMode: z.literal('require-host') }).strict(),
  z.object({
    hostMode: z.literal('prefer-host'),
    preferWaitSeconds: z.number().int()
      .min(MIN_PREFER_HOST_WAIT_SECONDS)
      .max(MAX_PREFER_HOST_WAIT_SECONDS),
  }).strict(),
  z.object({ hostMode: z.literal('any-mode') }).strict(),
]);

export const maintenancePolicySchema = z.discriminatedUnion('hostMode', [
  z.object({ hostMode: z.literal('require-host'), preferUntilUtc: z.null() }).strict(),
  z.object({ hostMode: z.literal('prefer-host'), preferUntilUtc: maintenanceUtcTimestampSchema }).strict(),
  z.object({ hostMode: z.literal('any-mode'), preferUntilUtc: z.null() }).strict(),
]);

export const maintenanceContextSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('machine-system'), sessionSid: z.null() }).strict(),
  z.object({ kind: z.literal('guest-session'), sessionSid: boundedId.nullable() }).strict(),
  z.object({ kind: z.literal('interactive-session'), sessionSid: boundedId }).strict(),
]);

export const maintenanceOperationSchema = z.enum([
  'update', 'repair', 'powershell', 'switch-campaign', 'validate-campaign','bootstrap-identity','bootstrap-repair',
]);
export const rerunPolicySchema = z.enum(['never-auto', 'idempotent', 'reconcile']);
export const maintenanceExecutorSchema = z.enum(['native-service', 'host-session']);

export const maintenanceArtifactSchema = z.object({
  artifactId: boundedId,
  sha256: maintenanceSha256Schema,
  byteLength: positiveSafeInteger,
  targetVersion: z.string().min(1).max(128).nullable(),
  installerVersion: z.string().min(1).max(128).optional(),
  branch: z.string().min(1).max(128).nullable(),
  kind: z.enum(['identity-bootstrap-only','immutable-recovery-repair']).optional(),
  identityBootstrap: maintenanceIdentityBootstrapSchema.optional(),
  repairBootstrap: maintenanceRepairBootstrapSchema.optional(),
}).strict();

export const maintenanceReconciliationTargetSchema = z.object({
  identity: maintenanceIdSchema,
  taskSha256: maintenanceSha256Schema,
}).strict();

export const maintenanceReconciliationDispositionSchema = z.enum([
  'not-started', 'completed', 'partial', 'unknown',
]);

export const maintenanceReconciliationRecordSchema = z.object({
  classifierIdentity: maintenanceIdSchema,
  classifierTaskSha256: maintenanceSha256Schema,
  eventId: boundedId,
  disposition: maintenanceReconciliationDispositionSchema,
  observedAtUtc: maintenanceUtcTimestampSchema,
}).strict();

export const signedMaintenanceEnvelopeSchema = z.object({
  algorithm: z.literal('Ed25519'),
  keyId: boundedId,
  payloadBase64: z.string().min(4).max(Math.ceil(MAX_MAINTENANCE_PAYLOAD_BYTES / 3) * 4),
  signatureBase64: z.string().max(128),
}).strict().superRefine((value, context) => {
  for (const property of ['payloadBase64', 'signatureBase64'] as const) {
    if (!isCanonicalBase64(value[property])) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [property],
        message: 'Expected canonical base64',
      });
    }
  }
  if (isCanonicalBase64(value.signatureBase64)
      && globalThis.atob(value.signatureBase64).length !== 64) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['signatureBase64'],
      message: 'Ed25519 signatures must be exactly 64 bytes',
    });
  }
});

export const maintenanceTaskSchema = maintenanceIdSchema.extend({
  protocolVersion: z.literal(MAINTENANCE_PROTOCOL_VERSION),
  operation: maintenanceOperationSchema,
  policy: maintenancePolicySchema,
  context: maintenanceContextSchema,
  artifact: maintenanceArtifactSchema,
  rerunPolicy: rerunPolicySchema,
  reconcileArtifact: maintenanceArtifactSchema.nullable(),
  timeoutSeconds: z.number().int().min(30).max(14_400).optional(),
  reconciliationFor: maintenanceReconciliationTargetSchema.optional(),
  notBeforeUtc: maintenanceUtcTimestampSchema,
  expiresAtUtc: maintenanceUtcTimestampSchema.nullable(),
  approvalRevision: nonnegativeSafeInteger,
  issuedAtUtc: maintenanceUtcTimestampSchema,
}).strict().superRefine((value, context) => {
  validateMaintenanceIdentityBootstrap(value as MaintenanceTask,context);
  validateMaintenanceRepairBootstrap(value as MaintenanceTask,context);
  if (!['update', 'repair'].includes(value.operation) && value.artifact.installerVersion !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'installerVersion'], message: 'Only valid for platform installers' });
  }
  if (value.reconcileArtifact?.installerVersion !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact', 'installerVersion'], message: 'Classifier cannot specify an installer version' });
  }
  if (value.expiresAtUtc !== null && Date.parse(value.notBeforeUtc) >= Date.parse(value.expiresAtUtc)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiresAtUtc'], message: 'Must follow notBeforeUtc' });
  }
  if (value.rerunPolicy === 'reconcile' && value.reconcileArtifact === null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact'], message: 'Required for reconcile' });
  }
  if (value.rerunPolicy !== 'reconcile' && value.reconcileArtifact !== null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact'], message: 'Only valid for reconcile' });
  }
  if (value.operation === 'powershell' && value.artifact.byteLength > MAX_MAINTENANCE_POWERSHELL_BYTES) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'byteLength'], message: 'PowerShell payload exceeds 16 MiB' });
  }
  if (value.operation !== 'bootstrap-repair' && value.artifact.repairBootstrap !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'repairBootstrap'], message: 'Only valid for bootstrap-repair' });
  }
  if (value.reconcileArtifact?.repairBootstrap !== undefined || value.reconcileArtifact?.kind === 'immutable-recovery-repair') {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact'], message: 'Repair bootstrap cannot be reconciled' });
  }
  if (value.reconciliationFor !== undefined) {
    if (value.operation !== 'powershell') {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['operation'], message: 'Reconciliation classifiers must use PowerShell' });
    }
    if (value.rerunPolicy !== 'never-auto' || value.reconcileArtifact !== null) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconciliationFor'], message: 'Classifier tasks cannot authorize another rerun' });
    }
    const sameIdentity = Object.entries(value.reconciliationFor.identity)
      .every(([key, item]) => value[key as keyof typeof value] === item);
    if (sameIdentity) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconciliationFor', 'identity'], message: 'Classifier must reference a prior attempt' });
    }
  }
});

export const maintenanceStartGrantSchema = maintenanceIdSchema.extend({
  protocolVersion: z.literal(MAINTENANCE_PROTOCOL_VERSION),
  taskSha256: maintenanceSha256Schema,
  nativeGeneration: nonnegativeSafeInteger,
  permitId: boundedId,
  executor: maintenanceExecutorSchema,
  sessionSid: boundedId.nullable(),
  issuedAtUtc: maintenanceUtcTimestampSchema,
  expiresAtUtc: maintenanceUtcTimestampSchema,
}).strict().superRefine((value, context) => {
  const ttl = Date.parse(value.expiresAtUtc) - Date.parse(value.issuedAtUtc);
  if (ttl <= 0 || ttl > MAX_START_GRANT_TTL_MS) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiresAtUtc'], message: 'Grant TTL must be 1-60000ms' });
  }
});

export const maintenanceCancellationSchema = maintenanceIdSchema.extend({
  protocolVersion: z.literal(MAINTENANCE_PROTOCOL_VERSION),
  issuedAtUtc: maintenanceUtcTimestampSchema,
  expiresAtUtc: maintenanceUtcTimestampSchema,
}).strict().refine((value) => Date.parse(value.issuedAtUtc) < Date.parse(value.expiresAtUtc), {
  path: ['expiresAtUtc'], message: 'Must follow issuedAtUtc',
});
export const maintenanceReconciliationSchema = maintenanceCancellationSchema;

export const hostLifecycleSchema = z.enum(['inactive', 'entering', 'active', 'exiting']);
export const nativeMaintenanceSnapshotSchema = z.object({
  hostLifecycle: hostLifecycleSchema,
  generation: nonnegativeSafeInteger,
  connectedPlayers: nonnegativeSafeInteger,
  recording: z.boolean(),
  activeAttemptId: boundedId.nullable(),
  sessionSid: boundedId.nullable(),
  observedAtUtc: maintenanceUtcTimestampSchema,
}).strict();

export const maintenanceExecutionSchema = z.object({
  operation: maintenanceOperationSchema,
  context: z.enum(['machine-system', 'guest-session', 'interactive-session']),
  executor: maintenanceExecutorSchema,
}).strict();

export const maintenanceCapabilitiesSchema = z.object({
  protocolVersion: z.literal(MAINTENANCE_PROTOCOL_VERSION),
  nativeProtocolVersion: z.literal(MAINTENANCE_PROTOCOL_VERSION).nullable(),
  policies: z.array(z.enum(['require-host', 'prefer-host', 'any-mode'])).max(3),
  contexts: z.array(z.enum(['machine-system', 'guest-session', 'interactive-session'])).max(3),
  operations: z.array(maintenanceOperationSchema).max(7),
  executions: z.array(maintenanceExecutionSchema).max(30),
  durableAttempts: z.boolean(),
  signedEnvelope: z.boolean(),
  nativeErrorCode: boundedId.optional(),
  serviceErrorCode: boundedId.optional(),
  supportsInstallerVersion: z.boolean().optional(),
  legacyCommandFencing: z.boolean().optional(),
}).strict().superRefine((value, context) => {
  for (const property of ['policies', 'contexts', 'operations'] as const) {
    if (new Set(value[property]).size !== value[property].length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: [property], message: 'Duplicate capability' });
    }
  }
  const executions = value.executions.map((item) => `${item.operation}\0${item.context}\0${item.executor}`);
  if (new Set(executions).size !== executions.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['executions'], message: 'Duplicate execution tuple' });
  }
});

export const preparationStateSchema = z.enum(['not-started', 'downloading', 'ready', 'failed']);
export const maintenanceExecutionStateSchema = z.enum([
  'waiting', 'starting', 'running', 'reboot-pending', 'verifying', 'succeeded', 'failed',
  'cancelled', 'expired', 'outcome-unknown',
]);
export const maintenanceWaitReasonValueSchema = z.enum([
  'host-mode', 'preference', 'session', 'players', 'recording', 'busy', 'not-before',
  'approval', 'unsupported-capability', 'bootstrap-required', 'update-disabled',
  'connectivity', 'storage', 'clock-uncertain',
]);
export const maintenanceWaitReasonSchema = maintenanceWaitReasonValueSchema.nullable();

export const maintenanceVersionEvidenceSchema = z.object({
  component: boundedId,
  version: z.string().min(1).max(128),
  source: z.enum(['live-service', 'registry', 'package']),
  observedAtUtc: maintenanceUtcTimestampSchema,
  bootId: boundedId,
}).strict();

export const maintenanceEventSchema = maintenanceIdSchema.extend({
  eventId: boundedId,
  sequence: nonnegativeSafeInteger,
  observedAtUtc: maintenanceUtcTimestampSchema,
  preparation: preparationStateSchema,
  execution: maintenanceExecutionStateSchema,
  waitReason: maintenanceWaitReasonSchema,
  bytesPrepared: nonnegativeSafeInteger,
  errorCode: boundedId.nullable(),
  nativeErrorCode: z.number().int().nullable(),
  installerExitCode: z.number().int().nullable(),
  rebootRequired: z.boolean(),
  evidence: z.array(maintenanceVersionEvidenceSchema).max(100),
  reconciliationResult: z.object({
    target: maintenanceReconciliationTargetSchema,
    disposition: maintenanceReconciliationDispositionSchema,
  }).strict().optional(),
}).strict().superRefine((value, context) => {
  if (value.reconciliationResult !== undefined
      && !['succeeded', 'failed', 'cancelled', 'expired', 'outcome-unknown'].includes(value.execution)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reconciliationResult'],
      message: 'Reconciliation results are terminal evidence',
    });
  }
});

export const maintenanceCommandSchema = z.object({
  sequence: nonnegativeSafeInteger,
  commandId: boundedId,
  kind: z.enum(['prepare', 'cancel', 'reconcile']),
  identity: maintenanceIdSchema,
  envelope: signedMaintenanceEnvelopeSchema,
}).strict();

export const maintenanceSyncRequestSchema = z.object({
  capabilities: maintenanceCapabilitiesSchema,
  afterCommandSequence: nonnegativeSafeInteger,
  events: z.array(maintenanceEventSchema).max(100),
  snapshot: nativeMaintenanceSnapshotSchema.nullable(),
  capabilityOnly: z.boolean().optional(),
}).strict();

export const maintenanceSyncReplySchema = z.object({
  commands: z.array(maintenanceCommandSchema).max(100),
  acknowledgedEventIds: z.array(boundedId).max(100),
  serverTimeUtc: maintenanceUtcTimestampSchema,
  retryAfterSeconds: z.number().int().min(0).max(3_600),
}).strict();

export const maintenanceJobViewSchema = z.object({
  identity: maintenanceIdSchema,
  task: maintenanceTaskSchema,
  delivery: z.enum(['queued', 'received', 'acknowledged']),
  latestEvent: maintenanceEventSchema.nullable(),
  capabilities: maintenanceCapabilitiesSchema.nullable(),
  cancellation: z.enum(['none', 'requested', 'confirmed', 'partial']),
  reconciliation: maintenanceReconciliationRecordSchema.nullable().optional(),
  events: z.array(maintenanceEventSchema).max(100),
  nextEventCursor: boundedId.nullable(),
}).strict();

export const maintenanceRequestLookupSchema = z.object({
  jobs: z.array(maintenanceJobViewSchema).max(10_000),
  pendingMachineIds: z.array(machineId).max(10_000),
  resource: z.object({ kind: z.literal('deployment'), id: boundedId }).strict().nullable(),
}).strict();

export const maintenanceStartReplySchema = z.discriminatedUnion('granted', [
  z.object({
    granted: z.literal(true),
    executor: maintenanceExecutorSchema,
    grant: signedMaintenanceEnvelopeSchema,
  }).strict(),
  z.object({
    granted: z.literal(false),
    waitReason: maintenanceWaitReasonValueSchema,
    retryAfterSeconds: z.number().int().min(0).max(3_600),
    errorCode: boundedId.nullable(),
  }).strict(),
]);

export const maintenanceSessionDispatchSchema = z.object({
  type: z.literal('PROVISIONING_MAINTENANCE_DEPLOY'),
  identity: maintenanceIdSchema,
  task: signedMaintenanceEnvelopeSchema,
  grant: signedMaintenanceEnvelopeSchema,
}).strict();

export const maintenanceCreateRequestSchema = z.object({
  machineIds: z.array(machineId).min(1).max(10_000).refine((ids) => new Set(ids).size === ids.length),
  operation: maintenanceOperationSchema,
  artifact: maintenanceArtifactSchema,
  context: maintenanceContextSchema,
  rerunPolicy: rerunPolicySchema,
  reconcileArtifact: maintenanceArtifactSchema.nullable(),
  timeoutSeconds: z.number().int().min(30).max(14_400).optional(),
  policy: maintenancePolicyInputSchema.optional(),
  notBeforeUtc: maintenanceUtcTimestampSchema.optional(),
  expiresAtUtc: maintenanceUtcTimestampSchema.nullable().optional(),
}).strict().superRefine((value, context) => {
  if (!['update', 'repair'].includes(value.operation) && value.artifact.installerVersion !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'installerVersion'], message: 'Only valid for platform installers' });
  }
  if (value.reconcileArtifact?.installerVersion !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact', 'installerVersion'], message: 'Classifier cannot specify an installer version' });
  }
  const hasReconciliation = value.reconcileArtifact !== null;
  if ((value.rerunPolicy === 'reconcile') !== hasReconciliation) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact'], message: 'Must match rerunPolicy' });
  }
  if (value.notBeforeUtc && value.expiresAtUtc
      && Date.parse(value.notBeforeUtc) >= Date.parse(value.expiresAtUtc)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiresAtUtc'], message: 'Must follow notBeforeUtc' });
  }
  if (value.operation === 'powershell' && value.artifact.byteLength > MAX_MAINTENANCE_POWERSHELL_BYTES) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'byteLength'], message: 'PowerShell payload exceeds 16 MiB' });
  }
  if (value.operation !== 'bootstrap-repair' && value.artifact.repairBootstrap !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['artifact', 'repairBootstrap'], message: 'Only valid for bootstrap-repair' });
  }
  if (value.reconcileArtifact?.repairBootstrap !== undefined || value.reconcileArtifact?.kind === 'immutable-recovery-repair') {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['reconcileArtifact'], message: 'Repair bootstrap cannot be reconciled' });
  }
});

export function resolveMaintenancePolicy(
  input: MaintenancePolicyInput | undefined,
  notBeforeUtc: string,
): MaintenancePolicy {
  const parsedStart = maintenanceUtcTimestampSchema.parse(notBeforeUtc);
  const parsed = maintenancePolicyInputSchema.parse(input ?? {
    hostMode: 'prefer-host', preferWaitSeconds: DEFAULT_PREFER_HOST_WAIT_SECONDS,
  });
  if (parsed.hostMode !== 'prefer-host') return { hostMode: parsed.hostMode, preferUntilUtc: null };
  const preferUntilUtc = new Date(Date.parse(parsedStart) + parsed.preferWaitSeconds * 1_000).toISOString();
  return { hostMode: parsed.hostMode, preferUntilUtc };
}

export function decodeCanonicalBase64(value: string, maximumBytes = MAX_MAINTENANCE_PAYLOAD_BYTES): Uint8Array {
  if (!isCanonicalBase64(value)) throw new Error('maintenance_base64_noncanonical');
  const binary = globalThis.atob(value);
  if (binary.length > maximumBytes) throw new Error('maintenance_payload_too_large');
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function parseMaintenancePayload<TSchema extends z.ZodTypeAny>(
  payloadBase64: string,
  schema: TSchema,
): z.infer<TSchema> {
  const bytes = decodeCanonicalBase64(payloadBase64);
  const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  assertNoDuplicateJsonKeys(text);
  return schema.parse(JSON.parse(text)) as z.infer<TSchema>;
}
